import React, { useEffect, useState, lazy, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, Check, FileText, Clock, Building2, Frown, GraduationCap, Search, Puzzle, Tornado, Home, Brain, ChevronDown, ChevronUp, ShieldCheck, MapPin, X } from 'lucide-react';
// Reusable FadeIn Wrapper for scroll animations
function FadeIn({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-50px'
  }} transition={{
    duration: 0.5,
    delay
  }} className={className} data-id="element-0">
      {children}
    </motion.div>;
}
export function App() {
  const [activeTomo, setActiveTomo] = useState<number | null>(0);
  return <div className="min-h-screen bg-sand-light text-ink font-sans overflow-x-hidden" data-id="element-1">
      {/* TOPBAR */}

      {/* HERO */}
      <header className="relative overflow-hidden bg-gradient-to-br from-petrol to-petrol-deep text-[#eaf1f3] pt-10 pb-12 md:pt-16 md:pb-16" data-id="element-2">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_10%,rgba(132,169,140,0.22),transparent_45%)] pointer-events-none" data-id="element-3" />
        <div className="max-w-[1080px] mx-auto px-6 relative z-10 text-center" data-id="element-4">
          <FadeIn data-id="element-5">
            <div className="inline-flex gap-2 items-center bg-white/10 border border-gold-light/30 text-gold-light py-1.5 px-4 rounded-full text-xs font-semibold tracking-wider mb-6" data-id="element-6">
              ★ SISTEMA N°1 PARA PSICOPEDAGOGOS EN ARGENTINA
            </div>

            <h1 className="text-sage font-bold text-[clamp(1.1rem,2.6vw,1.6rem)] tracking-normal mb-2 leading-tight" data-id="element-7">
              De la primera entrevista al alta, sin armar nada desde cero
            </h1>

            {/* Mockup principal — al 70% de su tamaño */}
            <motion.div animate={{
            y: [0, -10, 0]
          }} transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="w-[70%] max-w-[480px] mx-auto mt-4 mb-2" data-id="element-8">
              <img src="https://cdn.magicpatterns.com/uploads/cANKR7i6Ybw1chFy55R8nP/mockup_fondo_transparente_psicopedagogia.png" alt="Kit Profesional Psicopedagógico" className="w-full h-auto drop-shadow-2xl" loading="eager" fetchPriority="high" decoding="sync" width="1024" height="1024" data-id="element-9" />
            </motion.div>

            <p className="text-[0.72rem] font-semibold tracking-wide text-gold-light/90 mb-5" data-id="element-10">
              8 Tomos + 3 Bonos · Listos para usar
            </p>

            <p className="max-w-[620px] mx-auto mt-2 md:mt-4 text-base md:text-lg text-[#cddce0]" data-id="element-11">
              Todas las plantillas, informes y herramientas de tu consultorio
              resueltas: anamnesis, baterías de evaluación, modelos de informe,
              documentación de obra social, devolución a familias y gestión.
              Dejá de improvisar cada documento.
            </p>

            <div className="flex flex-wrap gap-2.5 justify-center my-6" data-id="element-12">
              {['PDF Digital', 'Acceso Inmediato', 'Adaptado a Argentina', 'Descargá y Aplicá'].map(pill => <span key={pill} className="bg-white/10 border border-white/15 py-1.5 px-4 rounded-full font-semibold text-[#e8f0f2] text-[9px] pt-[4px] pb-[4px] pl-[16px] pr-[16px]" data-id="element-13">
                  {pill}
                </span>)}
            </div>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-14">
            <a href="#ofertas" className="inline-block bg-gradient-to-br from-gold-light to-gold text-[#3b2e08] font-extrabold text-base md:text-lg tracking-wide py-4 px-8 md:px-10 rounded-xl shadow-[0_14px_30px_-10px_rgba(201,162,75,0.7)] hover:-translate-y-0.5 hover:shadow-[0_20px_40px_-12px_rgba(201,162,75,0.85)] transition-all duration-150 active:scale-95" data-id="element-15">
              💥 QUIERO ORDENAR MI CONSULTORIO AHORA
            </a>

            <div className="flex flex-wrap gap-x-6 gap-y-3 justify-center mt-7 text-sm text-[#bcccd0]" data-id="element-16">
              {['Garantía 7 días', 'Pago seguro', 'Mercado Pago', '+500 profesionales', '4.9 valoración'].map(trust => <span key={trust} className="flex items-center gap-1" data-id="element-17">
                  <Check size={16} className="text-sage" data-id="element-18" />{' '}
                  <b className="text-white" data-id="element-19">{trust}</b>
                </span>)}
            </div>
          </FadeIn>
        </div>
      </header>

      {/* TESTIMONIALS - Horizontal Scroll on Mobile */}
      <section className="bg-white py-10 md:py-16" data-id="element-20">
        <div className="max-w-[1080px] mx-auto px-6" data-id="element-21">
          <FadeIn data-id="element-22">
            <div className="flex overflow-x-auto snap-x snap-mandatory hide-scrollbar gap-4 pb-4 -mx-6 px-6 md:grid md:grid-cols-3 md:gap-6 md:overflow-visible md:snap-none md:pb-0 md:mx-0 md:px-0" data-id="element-23">
              {[{
              text: '"Ordené toda la documentación de mi consultorio en un fin de semana. Ahora completo un informe de evaluación en 20 minutos en vez de pasarme la tarde entera. Nunca más arranco una plantilla desde cero."',
              name: 'Carolina M.',
              role: 'La Plata — Consultorio particular'
            }, {
              text: '"Los modelos de informe en versión técnica y \'en criollo\' me cambiaron las devoluciones. Las familias entienden a la primera y dejé de improvisar lo que les digo. Una tranquilidad enorme."',
              name: 'Florencia R.',
              role: 'Córdoba — Psicopedagoga recién recibida'
            }, {
              text: '"Las planillas de obra social ya en el formato correcto me ahorraron rechazos de IOMA. Presento todo bien la primera vez. Esto solo ya vale lo que pagué por el kit completo."',
              name: 'Lucía F.',
              role: 'Rosario — Prestadora de obra social'
            }].map((testi, i) => <div key={i} className="snap-center shrink-0 w-[85vw] md:w-auto bg-sand-light border border-[#ece4d8] rounded-card p-6 shadow-[0_8px_24px_-18px_rgba(17,48,63,0.4)] flex flex-col" data-id="element-24">
                  <div className="text-gold flex gap-1 mb-3" data-id="element-25">
                    {[1, 2, 3, 4, 5].map(s => <Star key={s} size={16} fill="currentColor" data-id="element-26" />)}
                  </div>
                  <p className="text-[0.96rem] text-[#33444a] italic mb-4 flex-grow" data-id="element-27">
                    {testi.text}
                  </p>
                  <div className="mt-auto" data-id="element-28">
                    <div className="font-extrabold text-petrol" data-id="element-29">
                      {testi.name}
                    </div>
                    <div className="text-sm text-muted" data-id="element-30">{testi.role}</div>
                  </div>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* PAIN POINTS - 2 Row Horizontal Scroll on Mobile */}
      <section className="bg-gradient-to-b from-sand-light to-white py-10 md:py-16" data-id="element-31">
        <div className="max-w-[1080px] mx-auto px-6" data-id="element-32">
          <FadeIn data-id="element-33">
            <div className="text-center max-w-[640px] mx-auto mb-8 md:mb-11" data-id="element-34">
              <span className="inline-block text-xs font-bold tracking-[0.18em] uppercase text-sage-deep mb-3" data-id="element-35">
                ¿Te pasa esto?
              </span>
              <h2 className="text-[clamp(1.6rem,4vw,2.4rem)] font-extrabold text-petrol mb-3 tracking-tight" data-id="element-36">
                Si atendés en consultorio, esto te suena familiar
              </h2>
            </div>

            <div className="flex overflow-x-auto snap-x snap-mandatory hide-scrollbar gap-3 md:gap-4 pb-4 -mx-6 px-6" data-id="element-37">
              {[{
              icon: <FileText size={24} data-id="element-38" />,
              title: 'Armás todo desde cero',
              desc: 'Cada paciente nuevo es volver a redactar la misma anamnesis y el mismo informe.'
            }, {
              icon: <Clock size={24} data-id="element-39" />,
              title: 'El papeleo te roba horas',
              desc: 'Te llevás los informes a casa y los terminás de noche o el fin de semana.'
            }, {
              icon: <Building2 size={24} data-id="element-40" />,
              title: 'La obra social te rebota',
              desc: 'Presentás la planilla mal confeccionada y te demoran o rechazan la prestación.'
            }, {
              icon: <Frown size={24} data-id="element-41" />,
              title: 'La devolución te angustia',
              desc: 'El momento de hablar con la familia te genera ansiedad y lo improvisás cada vez.'
            }, {
              icon: <GraduationCap size={24} data-id="element-42" />,
              title: 'Posgrados sin material',
              desc: 'Te dieron teoría, pero al volver al consultorio seguís sin plantillas listas.'
            }, {
              icon: <Search size={24} data-id="element-43" />,
              title: 'Plantillas sueltas de internet',
              desc: 'Genéricas, de otro país, sin contemplar IOMA, PAMI ni el marco argentino.'
            }, {
              icon: <Puzzle size={24} data-id="element-44" />,
              title: 'Cada caso, un caos nuevo',
              desc: 'Sin un sistema repetible, todo depende de tu memoria y tu energía del día.'
            }, {
              icon: <Tornado size={24} data-id="element-45" />,
              title: 'Marzo te desborda',
              desc: 'Entran pacientes nuevos juntos y no llegás a sistematizar la evaluación a tiempo.'
            }].map((pain, i) => <div key={i} className="snap-center shrink-0 w-[75vw] sm:w-[45vw] md:w-[260px] bg-white border border-[#e9e1d5] rounded-xl p-5 flex flex-col" data-id="element-46">
                  <div className="text-petrol mb-2.5" data-id="element-47">{pain.icon}</div>
                  <h4 className="text-base font-bold text-petrol mb-1.5" data-id="element-48">
                    {pain.title}
                  </h4>
                  <p className="text-sm text-muted" data-id="element-49">{pain.desc}</p>
                </div>)}
            </div>

            <p className="text-center text-lg md:text-xl font-extrabold text-petrol-deep mt-6 md:mt-10" data-id="element-50">
              ¿<span className="text-danger" data-id="element-51">Cuántos informes más</span> vas a
              redactar desde cero?
            </p>
          </FadeIn>
        </div>
      </section>

      {/* FOR WHO - Horizontal Scroll on Mobile */}
      <section className="bg-white py-10 md:py-16" data-id="element-52">
        <div className="max-w-[1080px] mx-auto px-6" data-id="element-53">
          <FadeIn data-id="element-54">
            <div className="text-center max-w-[640px] mx-auto mb-8 md:mb-11" data-id="element-55">
              <span className="inline-block text-xs font-bold tracking-[0.18em] uppercase text-sage-deep mb-3" data-id="element-56">
                ¿Para quién es?
              </span>
              <h2 className="text-[clamp(1.6rem,4vw,2.4rem)] font-extrabold text-petrol mb-3 tracking-tight" data-id="element-57">
                Hecho para vos si querés profesionalizar tu consultorio
              </h2>
            </div>

            <div className="flex overflow-x-auto snap-x snap-mandatory hide-scrollbar gap-4 pb-4 -mx-6 px-6" data-id="element-58">
              <div className="snap-center shrink-0 w-[85vw] sm:w-[60vw] md:w-[340px] flex gap-4 items-start bg-sand-light border border-[#ece4d8] rounded-xl p-5 relative" data-id="element-59">
                <div className="text-petrol flex-shrink-0" data-id="element-60">
                  <GraduationCap size={28} data-id="element-61" />
                </div>
                <div data-id="element-62">
                  <h4 className="text-base font-bold text-petrol mb-1" data-id="element-63">
                    Psicopedagoga recién recibida
                  </h4>
                  <p className="text-sm text-muted" data-id="element-64">
                    Que arma su consultorio y quiere arrancar con un sistema
                    profesional, sin años de prueba y error.
                  </p>
                </div>
              </div>
              <div className="snap-center shrink-0 w-[85vw] sm:w-[60vw] md:w-[340px] flex gap-4 items-start bg-sand-light border border-[#ece4d8] rounded-xl p-5 relative" data-id="element-65">
                <div className="text-petrol flex-shrink-0" data-id="element-66">
                  <Home size={28} data-id="element-67" />
                </div>
                <div data-id="element-68">
                  <h4 className="text-base font-bold text-petrol mb-1" data-id="element-69">
                    Consultorio particular en marcha
                  </h4>
                  <p className="text-sm text-muted" data-id="element-70">
                    Con pacientes que ya facturan y necesitan recuperar las
                    horas que se van en papeleo.
                  </p>
                </div>
              </div>
              <div className="snap-center shrink-0 w-[85vw] sm:w-[60vw] md:w-[340px] flex gap-4 items-start bg-sand-light border border-[#ece4d8] rounded-xl p-5 relative" data-id="element-71">
                <span className="absolute top-3 right-3 bg-sage text-white text-[0.62rem] font-extrabold tracking-wider py-1 px-2 rounded-md" data-id="element-72">
                  CLAVE
                </span>
                <div className="text-petrol flex-shrink-0" data-id="element-73">
                  <Building2 size={28} data-id="element-74" />
                </div>
                <div data-id="element-75">
                  <h4 className="text-base font-bold text-petrol mb-1" data-id="element-76">
                    Prestadora de obra social
                  </h4>
                  <p className="text-sm text-muted" data-id="element-77">
                    Que pelea con planillas, renovaciones y formatos distintos
                    para cada obra social.
                  </p>
                </div>
              </div>
              <div className="snap-center shrink-0 w-[85vw] sm:w-[60vw] md:w-[340px] flex gap-4 items-start bg-sand-light border border-[#ece4d8] rounded-xl p-5 relative" data-id="element-78">
                <div className="text-petrol flex-shrink-0" data-id="element-79">
                  <Brain size={28} data-id="element-80" />
                </div>
                <div data-id="element-81">
                  <h4 className="text-base font-bold text-petrol mb-1" data-id="element-82">
                    Buscás respaldo clínico
                  </h4>
                  <p className="text-sm text-muted" data-id="element-83">
                    Querés herramientas de intervención listas para TDAH, TEA,
                    dislexia y discalculia.
                  </p>
                </div>
              </div>
            </div>

            <div className="text-center mt-4 md:mt-7" data-id="element-84">
              <a href="#garantia" className="text-sage-deep text-sm md:text-base font-bold border-b-2 border-sage pb-0.5 hover:text-petrol transition-colors" data-id="element-85">
                ¿No estás segura si es para vos? Tenés 7 días de garantía →
              </a>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* SYSTEM */}
      <section className="bg-gradient-to-br from-petrol to-petrol-deep text-[#e9f1f3] py-10 md:py-16" data-id="element-86">
        <div className="max-w-[1080px] mx-auto px-6" data-id="element-87">
          <FadeIn data-id="element-88">
            <div className="text-center max-w-[640px] mx-auto mb-8 md:mb-11" data-id="element-89">
              <span className="inline-block text-xs font-bold tracking-[0.18em] uppercase text-gold-light mb-3" data-id="element-90">
                El sistema completo
              </span>
              <h2 className="text-[clamp(1.6rem,4vw,2.4rem)] font-extrabold text-white mb-3 tracking-tight" data-id="element-91">
                Todo tu consultorio resuelto en un solo lugar
              </h2>
              <p className="text-[#c5d6da] text-base md:text-lg" data-id="element-92">
                8 Tomos + 3 Bonos · Documentación, evaluación, intervención y
                gestión · Todo listo para aplicar.
              </p>
            </div>

            <div className="grid grid-cols-2 md:flex md:flex-wrap gap-3 justify-center my-7" data-id="element-93">
              {[{
              val: '+100 pág.',
              lbl: 'de plantillas'
            }, {
              val: '8',
              lbl: 'Tomos'
            }, {
              val: '3',
              lbl: 'Bonos gratis'
            }, {
              val: '4',
              lbl: 'Dificultades'
            }].map((stat, i) => <div key={i} className="bg-white/10 border border-white/15 rounded-xl py-3.5 px-3 md:px-5 text-center md:min-w-[120px]" data-id="element-94">
                  <b className="block text-xl md:text-2xl text-gold-light font-extrabold" data-id="element-95">
                    {stat.val}
                  </b>
                  <span className="text-[0.7rem] md:text-xs text-[#bccdd1] tracking-wide" data-id="element-96">
                    {stat.lbl}
                  </span>
                </div>)}
            </div>

            <div className="flex flex-wrap gap-2 justify-center max-w-[760px] mx-auto" data-id="element-97">
              {['Anamnesis', 'Evaluación', 'Informes', 'Obra Social', 'Devolución', 'Gestión', 'Intervención', 'Estimulación', 'Vocacional', 'Familias'].map(chip => <span key={chip} className="bg-sage/20 border border-sage/40 text-[#dcebdf] py-1.5 px-3.5 rounded-full text-sm font-semibold" data-id="element-98">
                  {chip}
                </span>)}
            </div>

            <div className="mt-8" data-id="element-99">
              <h4 className="text-center text-gold-light text-xs tracking-[0.14em] uppercase font-bold mb-3" data-id="element-100">
                Herramientas incluidas
              </h4>
              <div className="flex flex-wrap gap-2 justify-center" data-id="element-101">
                {['TDAH', 'TEA', 'Dislexia', 'Discalculia', 'Funciones Ejecutivas', 'Conciencia Fonológica', 'Atención', 'Memoria de Trabajo', 'Orientación Vocacional', 'Consentimientos'].map(chip => <span key={chip} className="bg-white/5 border border-white/15 text-[#e3eef0] py-1 px-3 rounded-full text-xs font-medium" data-id="element-102">
                    {chip}
                  </span>)}
              </div>
            </div>

            <p className="text-center mt-7 text-[#bccdd1] text-[0.92rem]" data-id="element-103">
              Funciona desde hoy en tu consultorio, tu trabajo en escuela o tu
              atención online.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* MODULES - Interactive Accordion */}
      <section className="bg-sand-light py-10 md:py-16" data-id="element-104">
        <div className="max-w-[1080px] mx-auto px-6" data-id="element-105">
          <FadeIn data-id="element-106">
            <div className="text-center max-w-[640px] mx-auto mb-8 md:mb-11" data-id="element-107">
              <span className="inline-block text-xs font-bold tracking-[0.18em] uppercase text-sage-deep mb-3" data-id="element-108">
                Contenido completo
              </span>
              <h2 className="text-[clamp(1.6rem,4vw,2.4rem)] font-extrabold text-petrol mb-3 tracking-tight" data-id="element-109">
                Los 6 Tomos del Kit
              </h2>
              <p className="text-muted text-base md:text-lg" data-id="element-110">
                Tu hoja de ruta, en el orden real del proceso clínico. Toca para
                ver el detalle.
              </p>
            </div>

            <div className="flex flex-col gap-3 md:grid md:grid-cols-2 md:gap-4 md:items-start" data-id="element-111">
              {[{
              tag: 'TOMO 1',
              title: 'Primera Entrevista y Anamnesis',
              sub: 'El arranque de cada paciente, resuelto',
              desc: 'Plantillas listas para la entrevista inicial con la familia y la escuela.',
              img: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&w=100&q=80',
              items: ['Ficha de motivo de consulta', 'Anamnesis perinatal, madurativa y escolar', 'Cuestionario para el docente / EOE']
            }, {
              tag: 'TOMO 2',
              title: 'Batería de Evaluación Psicopedagógica',
              sub: 'Las 5 áreas, ordenadas',
              desc: 'Checklists y consignas por área, listas para llevar a sesión.',
              img: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?auto=format&fit=crop&w=100&q=80',
              items: ['Madurativa, intelectual, cognitiva, proyectiva, pedagógica', 'Banco de consignas por objetivo', 'Ficha de registro consolidado']
            }, {
              tag: 'TOMO 3',
              title: 'Modelos de Informe Psicopedagógico',
              sub: 'Completá y firmá',
              desc: 'Informe de evaluación, evolución y alta, en doble versión.',
              img: 'https://images.unsplash.com/photo-1586281380349-632531db7ed4?auto=format&fit=crop&w=100&q=80',
              items: ['Versión técnica (válida ante terceros)', 'Versión "en criollo" (para la familia)', 'Banco de frases para conclusiones']
            }, {
              tag: 'TOMO 4',
              title: 'Documentación para Obras Sociales',
              sub: 'Presentá bien la primera vez',
              desc: 'Planillas en el formato que piden las auditorías.',
              img: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?auto=format&fit=crop&w=100&q=80',
              items: ['Planilla de atención psicopedagógica', 'Plan de tratamiento y derivación', 'Adaptado a IOMA, PAMI y OSDE']
            }, {
              tag: 'TOMO 5',
              title: 'Guía de Devolución Diagnóstica',
              sub: 'El momento más temido, con guion',
              desc: 'Scripts de devolución por edad del paciente.',
              img: 'https://images.unsplash.com/photo-1516534775068-ba3e7458af70?auto=format&fit=crop&w=100&q=80',
              items: ['Frases textuales para cada franja etaria', 'Cómo manejar la negación de la familia', 'Cómo explicarle el diagnóstico al chico']
            }, {
              tag: 'TOMO 6',
              title: 'Gestión del Consultorio',
              sub: 'Lo administrativo, bajo control',
              desc: 'Herramientas de gestión para usar todos los días.',
              img: 'https://images.unsplash.com/photo-1507925922837-326f46a5c1f5?auto=format&fit=crop&w=100&q=80',
              items: ['Agenda y seguimiento de sesiones autorizadas', 'Checklist de matrícula y habilitación', 'Recibo y calculadora de honorarios']
            }].map((mod, i) => <div key={i} className="bg-white border border-[#e7dfd2] rounded-xl shadow-sm overflow-hidden relative" data-id="element-112">
                  <div className="absolute left-0 top-0 bottom-0 w-[4px] bg-gradient-to-b from-sage to-petrol" data-id="element-113" />
                  <button onClick={() => setActiveTomo(activeTomo === i ? null : i)} className="w-full text-left p-3 md:p-4 pl-4 md:pl-5 flex items-center justify-between gap-3 hover:bg-sand-light/50 transition-colors" data-id="element-114">
                    <div className="flex items-center gap-3 md:gap-4" data-id="element-115">
                      <img src={mod.img} alt={mod.title} className="w-12 h-12 md:w-14 md:h-14 rounded-lg object-cover flex-shrink-0 shadow-sm" loading="lazy" decoding="async" width="56" height="56" data-id="element-116" />
                      <div data-id="element-117">
                        <span className="inline-block text-[0.65rem] font-extrabold tracking-wider text-white bg-petrol py-0.5 px-2 rounded-md mb-1" data-id="element-118">
                          {mod.tag}
                        </span>
                        <h3 className="text-[0.95rem] md:text-[1.05rem] font-bold text-petrol leading-tight pr-2" data-id="element-119">
                          {mod.title}
                        </h3>
                      </div>
                    </div>
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full bg-sand-light flex items-center justify-center transition-transform duration-300 ${activeTomo === i ? 'rotate-180 bg-sage text-white' : 'text-petrol'}`} data-id="element-120">
                      <ChevronDown size={18} data-id="element-121" />
                    </div>
                  </button>
                  <AnimatePresence data-id="element-122">
                    {activeTomo === i && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} className="overflow-hidden" data-id="element-123">
                        <div className="p-4 md:p-5 pt-0 md:pt-0 pl-4 md:pl-5 ml-12 md:ml-14" data-id="element-124">
                          <div className="pt-3 border-t border-sand" data-id="element-125">
                            <div className="text-[0.82rem] text-sage-deep font-bold mb-2" data-id="element-126">
                              {mod.sub}
                            </div>
                            <p className="text-[0.85rem] md:text-[0.9rem] text-[#48585e] mb-3" data-id="element-127">
                              {mod.desc}
                            </p>
                            <ul className="flex flex-col gap-1.5" data-id="element-128">
                              {mod.items.map((item, j) => <li key={j} className="text-[0.82rem] md:text-[0.86rem] text-[#3a4a50] pl-5 relative" data-id="element-129">
                                  <span className="absolute left-0 text-sage-deep font-extrabold" data-id="element-130">
                                    ✓
                                  </span>
                                  {item}
                                </li>)}
                            </ul>
                          </div>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>)}
            </div>

            <p className="text-center text-muted text-[0.86rem] mt-7" data-id="element-131">
              Y en el Megapack sumás 2 Tomos ampliados de intervención clínica
              👇
            </p>
          </FadeIn>
        </div>
      </section>

      {/* BONUSES - Horizontal Scroll on Mobile */}
      <section className="bg-white py-10 md:py-16" data-id="element-132">
        <div className="max-w-[1080px] mx-auto px-6" data-id="element-133">
          <FadeIn data-id="element-134">
            <div className="text-center max-w-[640px] mx-auto mb-8 md:mb-11" data-id="element-135">
              <span className="inline-block text-xs font-bold tracking-[0.18em] uppercase text-sage-deep mb-3" data-id="element-136">
                Incluidos gratis
              </span>
              <h2 className="text-[clamp(1.6rem,4vw,2.4rem)] font-extrabold text-petrol mb-3 tracking-tight" data-id="element-137">
                3 Bonos Estratégicos
              </h2>
              <p className="text-muted text-base md:text-lg" data-id="element-138">
                Sin costo extra — incluidos en las dos ofertas.
              </p>
            </div>

            <div className="flex overflow-x-auto snap-x snap-mandatory hide-scrollbar gap-4 pb-4 -mx-6 px-6" data-id="element-139">
              {[{
              bn: 'BONO 1',
              title: 'Folletos para Familias',
              desc: 'Material psicoeducativo listo para entregar tras la devolución: TDAH, TEA, dislexia y discalculia explicados en lenguaje claro para los padres.',
              img: 'https://images.unsplash.com/photo-1532012197267-da84d127e765?auto=format&fit=crop&w=150&q=80'
            }, {
              bn: 'BONO 2',
              title: 'Comunicación con la Escuela',
              desc: 'Notas, informes de enlace y sugerencias de adecuaciones curriculares listas para enviar a la institución, en el tono institucional correcto.',
              img: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=150&q=80'
            }, {
              bn: 'BONO 3',
              title: 'Consentimientos + Calculadora',
              desc: 'Modelos de consentimiento informado (Ley 26.529) y calculadora de honorarios particular vs. obra social con punto de equilibrio.',
              img: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=150&q=80'
            }].map((bonus, i) => <div key={i} className="snap-center shrink-0 w-[85vw] sm:w-[60vw] md:w-[340px] bg-sand-light border border-dashed border-sage rounded-xl p-5 md:p-6 relative flex flex-col" data-id="element-140">
                  <span className="absolute top-4 right-4 bg-sage text-white text-[0.62rem] font-extrabold tracking-wider py-1 px-2.5 rounded-md z-10" data-id="element-141">
                    GRATIS
                  </span>

                  <img src={bonus.img} alt={bonus.title} className="w-full h-32 object-cover rounded-lg mb-4 shadow-sm" loading="lazy" decoding="async" width="300" height="128" data-id="element-142" />

                  <div className="text-[0.7rem] font-extrabold text-sage-deep tracking-wider mb-1.5" data-id="element-143">
                    {bonus.bn}
                  </div>
                  <h4 className="text-[1.05rem] font-bold text-petrol mb-2" data-id="element-144">
                    {bonus.title}
                  </h4>
                  <p className="text-[0.85rem] text-muted leading-snug" data-id="element-145">
                    {bonus.desc}
                  </p>
                </div>)}
            </div>

            <p className="text-center mt-6 md:mt-8 text-lg md:text-xl font-extrabold text-petrol" data-id="element-146">
              VALOR DE LOS BONOS:{' '}
              <span className="text-sage-deep" data-id="element-147">incluidos sin cargo</span>
            </p>
          </FadeIn>
        </div>
      </section>

      {/* COMPARISON */}
      <section className="bg-gradient-to-b from-white to-sand-light py-10 md:py-16" data-id="element-148">
        <div className="max-w-[1080px] mx-auto px-6" data-id="element-149">
          <FadeIn data-id="element-150">
            <div className="text-center max-w-[640px] mx-auto mb-8 md:mb-11" data-id="element-151">
              <span className="inline-block text-xs font-bold tracking-[0.18em] uppercase text-sage-deep mb-3" data-id="element-152">
                La realidad
              </span>
              <h2 className="text-[clamp(1.6rem,4vw,2.4rem)] font-extrabold text-petrol mb-3 tracking-tight" data-id="element-153">
                ¿Cuánto te cuesta NO tenerlo?
              </h2>
            </div>

            <div className="overflow-x-auto hide-scrollbar -mx-6 px-6 md:mx-0 md:px-0" data-id="element-154">
              <div className="min-w-[600px] max-w-[840px] mx-auto bg-white rounded-card overflow-hidden shadow-soft border border-[#e7dfd2]" data-id="element-155">
                <div className="grid grid-cols-[1.4fr_1fr_1fr]" data-id="element-156">
                  <div className="p-4 font-extrabold text-[0.85rem] bg-petrol-deep text-white text-left" data-id="element-157">
                    Tu semana
                  </div>
                  <div className="p-4 font-extrabold text-[0.85rem] text-center bg-[#f3e9e6] text-danger" data-id="element-158">
                    Sin el Kit
                  </div>
                  <div className="p-4 font-extrabold text-[0.85rem] text-center bg-sage text-white" data-id="element-159">
                    Con el Kit
                  </div>
                </div>
                {[{
                feat: 'Redactar un informe',
                no: 'Una tarde entera',
                yes: '20 minutos'
              }, {
                feat: 'Armar la evaluación',
                no: 'Desde cero cada vez',
                yes: 'Batería lista por área'
              }, {
                feat: 'Planillas de obra social',
                no: 'Rebotes y demoras',
                yes: 'Formato correcto a la primera'
              }, {
                feat: 'La devolución a la familia',
                no: 'Improvisás y te angustia',
                yes: 'Guion listo por edad'
              }, {
                feat: 'Material para los padres',
                no: 'No tenés qué entregar',
                yes: 'Folletos listos para imprimir'
              }, {
                feat: 'Tu tiempo libre',
                no: 'Papeleo en casa de noche',
                yes: 'Te llevás el trabajo terminado'
              }].map((row, i) => <div key={i} className="grid grid-cols-[1.4fr_1fr_1fr] border-t border-[#efe7da]" data-id="element-160">
                    <div className="p-3.5 text-[0.86rem] text-[#3a4a50] font-semibold" data-id="element-161">
                      {row.feat}
                    </div>
                    <div className="p-3.5 text-[0.86rem] text-center text-[#9a6b66] bg-[#fbf6f5]" data-id="element-162">
                      {row.no}
                    </div>
                    <div className="p-3.5 text-[0.86rem] text-center text-[#37624a] bg-[#f3f7f3] font-semibold" data-id="element-163">
                      {row.yes}
                    </div>
                  </div>)}
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* OFFER / PRICING - Tab Toggle on Mobile */}
      <section id="ofertas" className="bg-gradient-to-br from-petrol to-petrol-deep text-[#eaf1f3] py-10 md:py-16" data-id="element-164">
        <div className="max-w-[1080px] mx-auto px-6" data-id="element-165">
          <FadeIn data-id="element-166">
            <div className="text-center mb-8" data-id="element-167">
              <span className="inline-block bg-gold-light text-[#3b2e08] font-extrabold text-[0.78rem] tracking-wider py-1.5 px-4 rounded-lg mb-3" data-id="element-168">
                ⏳ PRECIO DE LANZAMIENTO
              </span>
              <h2 className="text-[clamp(1.5rem,4vw,2.2rem)] font-extrabold text-white" data-id="element-169">
                Elegí tu Kit Profesional Psicopedagógico
              </h2>
              <p className="text-[#c5d6da] text-[0.9rem] mt-1.5" data-id="element-170">
                Las dos opciones incluyen los 3 bonos estratégicos completos
              </p>
            </div>

            <UrgencyBanner data-id="element-171" />
            <OfferSelector data-id="element-172" />
          </FadeIn>
        </div>
      </section>

      {/* TIER 2 DETAIL - Horizontal Scroll on Mobile */}
      <section className="bg-white py-10 md:py-16" data-id="element-173">
        <div className="max-w-[1080px] mx-auto px-6" data-id="element-174">
          <FadeIn data-id="element-175">
            <div className="text-center max-w-[640px] mx-auto mb-8 md:mb-11" data-id="element-176">
              <span className="inline-block text-xs font-bold tracking-[0.18em] uppercase text-sage-deep mb-3" data-id="element-177">
                ¿Qué suma el Megapack?
              </span>
              <h2 className="text-[clamp(1.6rem,4vw,2.4rem)] font-extrabold text-petrol mb-3 tracking-tight" data-id="element-178">
                El respaldo clínico que te diferencia
              </h2>
              <p className="text-muted text-base md:text-lg" data-id="element-179">
                Para resolver lo administrativo <em data-id="element-180">y</em> lo clínico en un solo
                lugar.
              </p>
            </div>

            <div className="flex overflow-x-auto snap-x snap-mandatory hide-scrollbar gap-4 md:gap-5 pb-4 -mx-6 px-6 max-w-[880px] mx-auto" data-id="element-181">
              <div className="snap-center shrink-0 w-[85vw] sm:w-[70vw] md:w-[420px] bg-sand-light border border-[#e7dfd2] rounded-2xl p-5 md:p-6 flex flex-col sm:flex-row gap-4 md:gap-5" data-id="element-182">
                <img src="https://images.unsplash.com/photo-1606092195730-5d7b9af1efc5?auto=format&fit=crop&w=150&q=80" alt="Banco de Intervención Clínica" className="w-full sm:w-24 h-32 sm:h-24 object-cover rounded-xl shadow-sm flex-shrink-0" loading="lazy" decoding="async" width="150" height="150" data-id="element-183" />
                <div data-id="element-184">
                  <div className="text-[0.7rem] font-extrabold tracking-wider text-sage-deep mb-1.5" data-id="element-185">
                    TOMO 7 · EXCLUSIVO MEGAPACK
                  </div>
                  <h4 className="text-[1.05rem] md:text-[1.1rem] font-bold text-petrol mb-1.5 leading-tight" data-id="element-186">
                    Banco de Intervención Clínica
                  </h4>
                  <p className="text-[0.85rem] md:text-[0.88rem] text-[#48585e] leading-snug" data-id="element-187">
                    Señales de alerta por edad, estrategias y actividades
                    aplicables para TDAH, TEA, dislexia y discalculia. Con
                    criterios de derivación al especialista y pautas para
                    familia y escuela.
                  </p>
                  <span className="inline-block mt-3 text-[0.74rem] font-bold text-white bg-petrol py-1 px-3 rounded-md" data-id="element-188">
                    20 páginas
                  </span>
                </div>
              </div>
              <div className="snap-center shrink-0 w-[85vw] sm:w-[70vw] md:w-[420px] bg-sand-light border border-[#e7dfd2] rounded-2xl p-5 md:p-6 flex flex-col sm:flex-row gap-4 md:gap-5" data-id="element-189">
                <img src="https://images.unsplash.com/photo-1494059980473-813e73ee784b?auto=format&fit=crop&w=150&q=80" alt="Estimulación Cognitiva y Orientación Vocacional" className="w-full sm:w-24 h-32 sm:h-24 object-cover rounded-xl shadow-sm flex-shrink-0" loading="lazy" decoding="async" width="150" height="150" data-id="element-190" />
                <div data-id="element-191">
                  <div className="text-[0.7rem] font-extrabold tracking-wider text-sage-deep mb-1.5" data-id="element-192">
                    TOMO 8 · EXCLUSIVO MEGAPACK
                  </div>
                  <h4 className="text-[1.05rem] md:text-[1.1rem] font-bold text-petrol mb-1.5 leading-tight" data-id="element-193">
                    Estimulación Cognitiva y Orientación Vocacional
                  </h4>
                  <p className="text-[0.85rem] md:text-[0.88rem] text-[#48585e] leading-snug" data-id="element-194">
                    Fichas listas para imprimir de memoria, atención y funciones
                    ejecutivas, más un proceso de orientación vocacional exprés
                    para adolescentes con informe final modelo.
                  </p>
                  <span className="inline-block mt-3 text-[0.74rem] font-bold text-white bg-petrol py-1 px-3 rounded-md" data-id="element-195">
                    20 páginas
                  </span>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* GUARANTEE */}
      <section id="garantia" className="bg-gradient-to-br from-sage-deep to-petrol text-[#eef4ef] text-center py-10 md:py-16" data-id="element-196">
        <div className="max-w-[1080px] mx-auto px-6" data-id="element-197">
          <FadeIn data-id="element-198">
            <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-white/10 border-2 border-white/40 flex items-center justify-center text-[0.75rem] md:text-[0.8rem] font-extrabold leading-tight mx-auto mb-5 text-white text-center" data-id="element-199">
              7 DÍAS
              <br data-id="element-200" />
              GARANTÍA
            </div>
            <h2 className="text-[clamp(1.5rem,4vw,2.1rem)] font-extrabold text-white mb-3.5" data-id="element-201">
              7 días para probarlo o te devolvemos el 100%
            </h2>
            <p className="max-w-[580px] mx-auto text-[#e0ece2] text-sm md:text-base" data-id="element-202">
              Descargás los tomos, abrís las plantillas y las usás con un
              paciente real. Si no es lo que esperabas: sin preguntas, sin
              formularios, sin vueltas. Tu compra está protegida.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-sand-light py-10 md:py-16" data-id="element-203">
        <div className="max-w-[1080px] mx-auto px-6" data-id="element-204">
          <FadeIn data-id="element-205">
            <div className="text-center max-w-[640px] mx-auto mb-8 md:mb-11" data-id="element-206">
              <span className="inline-block text-xs font-bold tracking-[0.18em] uppercase text-sage-deep mb-3" data-id="element-207">
                Dudas comunes
              </span>
              <h2 className="text-[clamp(1.6rem,4vw,2.4rem)] font-extrabold text-petrol mb-3 tracking-tight" data-id="element-208">
                Preguntas frecuentes
              </h2>
            </div>

            <div className="max-w-[780px] mx-auto flex flex-col gap-3" data-id="element-209">
              {[{
              q: '¿Cómo recibo el kit después de comprar?',
              a: 'Apenas se confirma el pago por Mercado Pago, recibís el acceso de descarga inmediata por email. Son archivos PDF que podés guardar en tu compu, celular o tablet y usar para siempre.'
            }, {
              q: '¿Sirve si recién me recibo o es solo para quien ya tiene consultorio?',
              a: 'Sirve en los dos casos. Si recién arrancás, te ahorra años de prueba y error armando tu propio sistema. Si ya tenés consultorio, te recupera las horas que hoy se van en papeleo.'
            }, {
              q: '¿Las plantillas están adaptadas a Argentina?',
              a: 'Sí. Todo está pensado para el marco argentino: terminología rioplatense, formatos de IOMA, PAMI y OSDE, referencia a la Ley 26.529 y la realidad del consultorio local. No son plantillas genéricas de otro país.'
            }, {
              q: '¿Los 3 bonos están incluidos en las dos ofertas?',
              a: 'Sí, los 3 bonos (folletos para familias, comunicación con la escuela y consentimientos + calculadora) están incluidos tanto en el Kit Profesional como en el Megapack.'
            }, {
              q: '¿Qué diferencia hay entre el Kit y el Megapack?',
              a: 'El Kit incluye los 6 tomos del día a día del consultorio + 3 bonos. El Megapack suma 2 tomos ampliados de 20 páginas: el Banco de Intervención Clínica (TDAH, TEA, dislexia, discalculia) y el de Estimulación Cognitiva y Orientación Vocacional.'
            }, {
              q: '¿Puedo editar las plantillas con mis datos?',
              a: 'Sí. Están pensadas como "descargá y aplicá": completás con los datos de tu paciente y de tu consultorio, sumás tu firma y matrícula, y quedan listas para usar o presentar.'
            }, {
              q: '¿El kit reemplaza mi criterio profesional?',
              a: 'No. Es una caja de herramientas que te respalda y te ahorra tiempo, pero el criterio clínico siempre es tuyo. El Banco de Intervención aclara que el diagnóstico formal requiere evaluación interdisciplinaria.'
            }, {
              q: '¿Tiene garantía real?',
              a: 'Sí, 7 días reales. Si no es lo que esperabas, te devolvemos el 100% sin preguntas ni trámites.'
            }].map((faq, i) => <FaqItem key={i} question={faq.q} answer={faq.a} data-id="element-210" />)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-gradient-to-br from-petrol to-petrol-deep text-[#eaf1f3] text-center py-10 md:py-16" data-id="element-211">
        <div className="max-w-[1080px] mx-auto px-6" data-id="element-212">
          <FadeIn data-id="element-213">
            <h2 className="text-[clamp(1.6rem,4vw,2.3rem)] font-extrabold text-white mb-2" data-id="element-214">
              Dejá de armar cada informe desde cero
            </h2>
            <p className="text-[#c5d6da] mb-6 md:mb-8" data-id="element-215">
              Tu consultorio ordenado este fin de semana. Acceso inmediato.
            </p>

            <UrgencyBanner data-id="element-216" />
            <OfferSelector data-id="element-217" />
          </FadeIn>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-petrol-deep text-[#aebfc4] py-10 md:py-12 text-center" data-id="element-218">
        <div className="max-w-[1080px] mx-auto px-6" data-id="element-219">
          <div className="text-lg font-extrabold text-white mb-2.5" data-id="element-220">
            📋 Kit Profesional Psicopedagógico
          </div>
          <p className="max-w-[560px] mx-auto mb-4 text-[0.86rem]" data-id="element-221">
            El sistema de gestión clínica N°1 para psicopedagogas en Argentina.
            Porque ordenar tu consultorio no debería costarte tus fines de
            semana.
          </p>
          <div className="text-[0.84rem] text-[#cdd8db] mb-3.5 flex flex-col sm:flex-row justify-center gap-2 sm:gap-4" data-id="element-222">
            <span data-id="element-223">contacto@solucionesya.shop</span>
            <span className="hidden sm:inline" data-id="element-224">•</span>
            <span data-id="element-225">@solucionesya.shop</span>
            <span className="hidden sm:inline" data-id="element-226">•</span>
            <span data-id="element-227">solucionesya.shop</span>
          </div>
          <div className="text-[0.76rem] text-[#7d9197] mt-3.5" data-id="element-228">
            © Solucionesya.shop 2026 — Todos los derechos reservados.
          </div>
        </div>
      </footer>

      {/* Prueba social — pop-up de compra verificada */}
      <PurchasePopup data-id="element-229" />
    </div>;
}
type OfferKey = 'kit' | 'megapack';
const OFFER_PLANS: Record<OfferKey, {
  name: string;
  desc: string;
  oldPrice: string;
  price: string;
  url: string;
  badge?: string;
}> = {
  kit: {
    name: 'Kit Profesional',
    desc: '6 Tomos + 3 Bonos estratégicos',
    oldPrice: '$29.990',
    price: '$19.990',
    url: 'https://checkout.solucionesya.shop/cart/49635652043000:1'
  },
  megapack: {
    name: 'Megapack Profesional',
    desc: 'Todo + 2 Tomos de intervención clínica',
    oldPrice: '$35.990',
    price: '$27.990',
    url: 'https://checkout.solucionesya.shop/cart/49635655254264:1',
    badge: 'MÁS ELEGIDA · AHORRÁS $8.000'
  }
};
function UrgencyBanner() {
  const TOTAL = 30 * 60; // 30 minutos en segundos
  const [secondsLeft, setSecondsLeft] = useState(TOTAL);
  useEffect(() => {
    const id = setInterval(() => {
      setSecondsLeft(s => s > 0 ? s - 1 : 0);
    }, 1000);
    return () => clearInterval(id);
  }, []);
  const mm = String(Math.floor(secondsLeft / 60)).padStart(2, '0');
  const ss = String(secondsLeft % 60).padStart(2, '0');
  // La barra arranca en 77% y sube gradualmente hasta ~96% mientras corre el tiempo
  const elapsed = 1 - secondsLeft / TOTAL;
  const progress = Math.min(96, 77 + elapsed * 19);
  return <div className="relative max-w-[680px] mx-auto mb-5 rounded-2xl overflow-hidden" data-id="element-230">
      {/* Borde pulsante llamativo */}
      <motion.div aria-hidden="true" animate={{
      opacity: [0.55, 1, 0.55]
    }} transition={{
      duration: 1.6,
      repeat: Infinity,
      ease: 'easeInOut'
    }} className="absolute inset-0 rounded-2xl ring-2 ring-gold-light pointer-events-none" data-id="element-231" />
      <div className="relative bg-gradient-to-br from-[#c0392b] via-[#b4524a] to-[#a23b34] text-white p-4 md:p-5 text-left" data-id="element-232">
        {/* Encabezado */}
        <div className="flex items-center justify-center gap-2 mb-3" data-id="element-233">
          <motion.span animate={{
          scale: [1, 1.2, 1],
          rotate: [0, -8, 8, 0]
        }} transition={{
          duration: 1.2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="text-lg leading-none" data-id="element-234">
            🔥
          </motion.span>
          <span className="text-[0.8rem] md:text-[0.92rem] font-extrabold uppercase tracking-wider text-center" data-id="element-235">
            ¡Últimos cupos de lanzamiento!
          </span>
          <span className="w-2 h-2 rounded-full bg-gold-light animate-pulse" data-id="element-236" />
        </div>

        {/* Timer + barra en una fila compacta */}
        <div className="flex items-center gap-3 md:gap-4" data-id="element-237">
          {/* Countdown */}
          <div className="flex-shrink-0 text-center" data-id="element-238">
            <div className="text-[0.6rem] uppercase tracking-wider text-white/80 mb-1 font-bold" data-id="element-239">
              Termina en
            </div>
            <div className="flex items-center gap-1 font-extrabold tabular-nums leading-none" data-id="element-240">
              <span className="bg-black/30 rounded-lg px-2 py-1.5 text-xl md:text-2xl" data-id="element-241">
                {mm}
              </span>
              <span className="text-lg md:text-xl" data-id="element-242">:</span>
              <span className="bg-black/30 rounded-lg px-2 py-1.5 text-xl md:text-2xl" data-id="element-243">
                {ss}
              </span>
            </div>
          </div>

          {/* Barra de carga de cupos */}
          <div className="flex-grow min-w-0" data-id="element-244">
            <div className="flex items-center justify-between text-[0.62rem] md:text-[0.7rem] font-bold mb-1" data-id="element-245">
              <span className="uppercase tracking-wide" data-id="element-246">
                Cupos casi agotados
              </span>
              <span className="text-gold-light" data-id="element-247">{Math.round(progress)}%</span>
            </div>
            <div className="h-2.5 md:h-3 w-full bg-black/30 rounded-full overflow-hidden" data-id="element-248">
              <motion.div className="h-full bg-gradient-to-r from-gold-light to-gold rounded-full" initial={{
              width: '77%'
            }} animate={{
              width: `${progress}%`
            }} transition={{
              ease: 'linear',
              duration: 1
            }} data-id="element-249" />
            </div>
          </div>
        </div>

        {/* Advertencia de suba de precio */}
        <p className="text-[0.74rem] md:text-[0.82rem] text-center mt-3 font-semibold text-white/95" data-id="element-250">
          ⚠️ Cuando se agoten los cupos, el precio sube{' '}
          <b className="text-gold-light" data-id="element-251">$10.000</b> y no vuelve a bajar.
        </p>
      </div>
    </div>;
}
function OfferSelector() {
  // Higher-priced option (megapack) preselected
  const [selected, setSelected] = useState<OfferKey>('megapack');
  const activePlan = OFFER_PLANS[selected];
  const renderCard = (key: OfferKey) => {
    const plan = OFFER_PLANS[key];
    const isSelected = selected === key;
    const isFeatured = key === 'megapack';
    return <button type="button" onClick={() => setSelected(key)} aria-pressed={isSelected} className={`relative w-full text-left rounded-2xl p-3.5 md:p-4 transition-all duration-200 flex flex-row items-center gap-3 md:gap-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-light ${isSelected ? 'bg-white border-[3px] border-gold-light shadow-soft' : 'bg-white/90 border-[3px] border-transparent opacity-80 hover:opacity-100'}`} data-id="element-252">
        {isFeatured && <span className="absolute -top-2.5 left-4 bg-gradient-to-br from-gold-light to-gold text-[#3b2e08] font-extrabold text-[0.6rem] md:text-[0.68rem] tracking-wide py-1 px-2.5 md:px-3 rounded-full whitespace-nowrap shadow-sm" data-id="element-253">
            ⭐ {plan.badge}
          </span>}
        {/* Radio indicator */}
        <span className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${isSelected ? 'border-gold bg-gold' : 'border-[#c9c1b4] bg-transparent'}`} data-id="element-254">
          {isSelected && <Check size={12} className="text-white" strokeWidth={3.5} data-id="element-255" />}
        </span>

        {/* Nombre + descripción */}
        <div className="flex-grow min-w-0" data-id="element-256">
          <h3 className="text-[0.95rem] md:text-base font-extrabold text-petrol leading-tight" data-id="element-257">
            {plan.name}
          </h3>
          <p className="text-[0.72rem] md:text-[0.8rem] text-muted leading-snug" data-id="element-258">
            {plan.desc}
          </p>
        </div>

        {/* Precios */}
        <div className="flex-shrink-0 text-right" data-id="element-259">
          <div className="text-[0.72rem] md:text-[0.8rem] text-[#b0a89c] line-through leading-none" data-id="element-260">
            {plan.oldPrice}
          </div>
          <div className="text-[1.35rem] md:text-[1.7rem] font-extrabold text-petrol leading-none mt-0.5" data-id="element-261">
            {plan.price}
          </div>
        </div>
      </button>;
  };
  return <div className="max-w-[680px] mx-auto" data-id="element-262">
      {/* Opciones en fila (apiladas) y horizontales */}
      <div className="flex flex-col gap-3 pt-3" data-id="element-263">
        {renderCard('megapack')}
        {renderCard('kit')}
      </div>

      {/* Shared buy button — redirects based on selection */}
      <a href={activePlan.url} className="mt-5 flex items-center justify-center text-center bg-gradient-to-br from-gold-light to-gold text-[#3b2e08] font-extrabold text-base md:text-[1.15rem] py-4 px-6 rounded-xl shadow-[0_14px_30px_-10px_rgba(201,162,75,0.7)] hover:-translate-y-0.5 transition-transform active:scale-95" data-id="element-264">
        💥 COMPRAR AHORA — {activePlan.price}
      </a>

      <div className="flex flex-wrap gap-x-5 gap-y-2 justify-center mt-5 text-[0.78rem] text-[#bccdd1]" data-id="element-265">
        {['Pago seguro', 'Acceso en 5 min', 'Garantía 7 días', 'Mercado Pago'].map(trust => <span key={trust} className="flex items-center gap-1" data-id="element-266">
            <Check size={15} className="text-sage" data-id="element-267" />{' '}
            <b className="text-white" data-id="element-268">{trust}</b>
          </span>)}
      </div>
    </div>;
}
function FaqItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="bg-white border border-[#e7dfd2] rounded-xl overflow-hidden" data-id="element-269">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full text-left py-4 px-5 font-bold text-petrol text-[0.96rem] flex justify-between items-center gap-3 hover:bg-sand-light/50 transition-colors" data-id="element-270">
        {question}
        <div className={`flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180 text-petrol' : 'text-sage-deep'}`} data-id="element-271">
          <ChevronDown size={20} data-id="element-272" />
        </div>
      </button>
      <AnimatePresence data-id="element-273">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} className="overflow-hidden" data-id="element-274">
            <div className="px-5 pb-5 pt-1 text-[#48585e] text-[0.9rem]" data-id="element-275">
              {answer}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
const POPUP_NAMES = ['Sofía G.', 'Martín P.', 'Valentina R.', 'Lucas M.', 'Camila S.', 'Mateo F.', 'Julieta A.', 'Tomás L.', 'Florencia D.', 'Agustín V.', 'Carla B.', 'Joaquín N.', 'Micaela T.', 'Federico H.', 'Antonella C.', 'Brenda Q.'];
const POPUP_PLACES = ['Buenos Aires', 'Córdoba', 'Rosario', 'La Plata', 'Mendoza', 'Mar del Plata', 'San Miguel de Tucumán', 'Salta', 'Santa Fe', 'Neuquén', 'Bahía Blanca', 'Resistencia', 'Posadas', 'San Juan', 'Paraná', 'Quilmes'];
const POPUP_OFFERS = ['el Kit Profesional', 'el Megapack Profesional'];
function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
type PurchaseNotice = {
  name: string;
  place: string;
  offer: string;
};
function buildNotice(): PurchaseNotice {
  return {
    name: pickRandom(POPUP_NAMES),
    place: pickRandom(POPUP_PLACES),
    offer: pickRandom(POPUP_OFFERS)
  };
}
function PurchasePopup() {
  const [visible, setVisible] = useState(false);
  const [notice, setNotice] = useState<PurchaseNotice>(buildNotice);
  useEffect(() => {
    let showTimer: ReturnType<typeof setTimeout>;
    let hideTimer: ReturnType<typeof setTimeout>;
    const cycle = () => {
      setNotice(buildNotice());
      setVisible(true);
      // Se mantiene visible ~6 segundos
      hideTimer = setTimeout(() => {
        setVisible(false);
        // Próxima aparición en un intervalo aleatorio de 40 a 60 segundos
        const nextDelay = 40000 + Math.random() * 20000;
        showTimer = setTimeout(cycle, nextDelay);
      }, 6000);
    };
    // Primera aparición tras un breve retraso inicial
    showTimer = setTimeout(cycle, 8000);
    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, []);
  return <AnimatePresence data-id="element-276">
      {visible && <motion.div role="status" aria-live="polite" initial={{
      opacity: 0,
      y: 24,
      x: -8
    }} animate={{
      opacity: 1,
      y: 0,
      x: 0
    }} exit={{
      opacity: 0,
      y: 24
    }} transition={{
      type: 'spring',
      stiffness: 260,
      damping: 24
    }} className="fixed z-50 bottom-4 left-4 right-4 sm:right-auto sm:max-w-[340px] bg-white border border-[#e7dfd2] rounded-2xl shadow-soft p-3.5 flex items-start gap-3" data-id="element-277">
          <div className="flex-shrink-0 w-11 h-11 rounded-full bg-sage/15 border border-sage/30 flex items-center justify-center text-sage-deep" data-id="element-278">
            <ShieldCheck size={22} data-id="element-279" />
          </div>

          <div className="min-w-0 flex-grow" data-id="element-280">
            <div className="flex items-center gap-1.5 mb-0.5" data-id="element-281">
              <span className="text-[0.7rem] font-extrabold uppercase tracking-wider text-sage-deep" data-id="element-282">
                Compra verificada
              </span>
              <span className="w-1.5 h-1.5 rounded-full bg-ok animate-pulse" data-id="element-283" />
            </div>
            <p className="text-[0.88rem] text-ink leading-snug" data-id="element-284">
              <b className="text-petrol" data-id="element-285">{notice.name}</b> acaba de comprar{' '}
              <b className="text-petrol" data-id="element-286">{notice.offer}</b>
            </p>
            <div className="flex items-center gap-1 mt-1 text-[0.74rem] text-muted" data-id="element-287">
              <MapPin size={12} className="text-sage-deep" data-id="element-288" />
              <span data-id="element-289">{notice.place}, Argentina</span>
              <span aria-hidden="true" data-id="element-290">·</span>
              <span data-id="element-291">hace unos segundos</span>
            </div>
          </div>

          <button type="button" onClick={() => setVisible(false)} aria-label="Cerrar notificación" className="flex-shrink-0 text-[#b9b0a2] hover:text-petrol transition-colors -mr-1 -mt-1 p-1" data-id="element-292">
            <X size={16} data-id="element-293" />
          </button>
        </motion.div>}
    </AnimatePresence>;
}